import classNames from "classnames";
import {forwardRef, useMemo, useState} from "react";

import {CollapsiblePanelProps} from "@/components/collapsiblePanel/CollapsiblePanel.types.ts";
import {CollapsiblePanelContext} from "@/components/collapsiblePanel/CollapsiblePanelContext.ts";
import {CollapsiblePanelToggleButton} from "@/components/collapsiblePanel/subComponents/CollapsiblePanelToggleButton/CollapsiblePanelToggleButton.tsx";

import styles from "./CollapsiblePanel.module.scss";

const CollapsiblePanelBase = forwardRef<HTMLDivElement, CollapsiblePanelProps>(
  (
    {defaultCollapsed = false, size = "medium", variant = "default", pinned, expandLabel, children, className, ...rest},
    ref
  ) => {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const contextValue = useMemo(
      () => ({collapsed, collapse: () => setCollapsed(true), expand: () => setCollapsed(false)}),
      [collapsed]
    );

    return (
      <CollapsiblePanelContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={classNames(
            styles.panel,
            styles[size],
            styles[variant],
            {[styles.collapsed]: collapsed},
            className
          )}
          {...rest}
        >
          <div className={styles.expandedContent} aria-hidden={collapsed}>
            {children}
          </div>
          <div className={styles.rail}>
            {pinned && <div className={styles.pinned}>{pinned}</div>}
            <div className={styles.railContent} aria-hidden={!collapsed}>
              <CollapsiblePanelToggleButton action="expand" label={expandLabel} />
            </div>
          </div>
          <div className={styles.insetShadow} />
        </div>
      </CollapsiblePanelContext.Provider>
    );
  }
);

CollapsiblePanelBase.displayName = "CollapsiblePanel";

export const CollapsiblePanel = Object.assign(CollapsiblePanelBase, {
  ToggleButton: CollapsiblePanelToggleButton,
});
